import { ChevronRightIcon } from '@heroicons/react/20/solid';
import React from 'react';

import { FadeSlideIn } from './Animations';
import { Button } from './Button';
import { cn } from './cn';

export interface BreadcrumbItem {
  key: string;
  text: string;
  onClick?: () => void;
  isCurrentItem?: boolean;
}

export interface PageHeaderProps {
  items?: BreadcrumbItem[];
  pageTitle: string;
  subtitle?: React.ReactNode;
  actionButtonProps?: {
    disabled?: boolean;
    icon?: React.ReactNode;
    onClick: () => void;
    title: string;
  };
  /** Custom right-aligned actions slot. Takes precedence over `actionButtonProps` when provided. */
  actions?: React.ReactNode;
  className?: string;
}

export const PageHeader = ({ items, pageTitle, subtitle, actionButtonProps, actions, className }: PageHeaderProps) => {
  return (
    <FadeSlideIn
      className={cn('page-container w-full flex items-center justify-between pt-6 pb-5', className)}
      direction="down"
    >
      <div>
        <h1 className="type-h1 text-text-primary">{pageTitle}</h1>
        {subtitle ? <p className="mt-1 type-body-sm text-text-secondary">{subtitle}</p> : null}
        {items?.length ? (
          <nav className="flex items-center gap-1.5 mt-1.5 type-body-sm text-text-muted" aria-label="Breadcrumb">
            {items.map((item, index) => (
              <React.Fragment key={item.key}>
                {index > 0 && <ChevronRightIcon className="h-4 w-4 text-text-disabled" />}
                {item.isCurrentItem ? (
                  <span className="text-text-secondary font-medium">{item.text}</span>
                ) : item.onClick ? (
                  <button
                    onClick={item.onClick}
                    className="hover:text-text-link transition-colors duration-200 cursor-pointer bg-transparent border-none p-0 type-body-sm text-text-muted"
                  >
                    {item.text}
                  </button>
                ) : (
                  // No target (e.g. an "Admin" section label) — render as static text, not a dead button.
                  <span className="type-body-sm text-text-muted">{item.text}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        ) : null}
      </div>
      {actions
        ? actions
        : actionButtonProps && (
            <Button variant="primary" onClick={actionButtonProps.onClick} disabled={actionButtonProps.disabled}>
              {actionButtonProps.icon}
              {actionButtonProps.title}
            </Button>
          )}
    </FadeSlideIn>
  );
};
